import { z } from "zod";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { useMemo, useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";

import { LoadingButton } from "@mui/lab";
import { Box, Grid, Stack, Dialog, Button, Divider, MenuItem, Typography, DialogTitle, DialogActions, DialogContent } from "@mui/material";

import { fData } from "src/utils/format-number";
import { phoneSchemaOpt } from "src/utils/zod-schema";
import axiosInstance, { endpoints } from "src/utils/axios";

import { useTranslate } from "src/locales";
import useCountries from "src/swr/use-countries";
import useCurrenciesLOV from "src/swr/use-currencies-lov";
import { createCompany, updateCompanyDetails } from "src/actions/company-details";

import { Form, Field } from "src/components/hook-form";
import { CloseButton } from "src/components/custom/buttons";


const CompanyDialog = ({ open, onClose, companyId, onExited, mutate }) => {
  const { t } = useTranslate();
  const isEditMode = !!companyId;

  const { data: countries = [] } = useCountries();
  const { data: currencies = [] } = useCurrenciesLOV();
  const [fetchedCompany, setFetchedCompany] = useState();

  const defaultValues = useMemo(() => ({
    name: '',
    countryId: '',
    currencyId: '',
    websiteUrl: '',
    formFile: null,
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    showContactName: true,
    showContactEmail: true,
    showContactPhone: true,
  }), [])

  const methods = useForm({
    mode: 'all',
    defaultValues,
    resolver: zodResolver(z.object({
      name: z.string()
        .min(3, 'Company name must be at least 3 characters.')
        .max(100, 'Company name must be at most 100 characters.'),
      countryId: z.string().min(1, 'Country is required!'),
      currencyId: z.string().min(1, 'Currency is required!'),
      websiteUrl: z.string().url('Website URL must be a valid URL.').nullish().or(z.literal('')),
      formFile: z.instanceof(File).nullish(),
      contactName: z.string().nullish().or(z.literal('')),
      contactEmail: z.string().email('Contact email must be a valid email.').nullish().or(z.literal('')),
      contactPhone: phoneSchemaOpt,
      showContactName: z.boolean(),
      showContactEmail: z.boolean(),
      showContactPhone: z.boolean(),
    })),
  })

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting }
  } = methods;

  useEffect(() => {
    if (companyId) {
      (async () => {
        try {
          const res = await axiosInstance.get(endpoints.auth.getCompanyDetails(companyId));
          const data = {
            ...res.data,
            websiteUrl: res.data.websiteUrl ?? '',
            contactName: res.data.contactName ?? '',
            contactEmail: res.data.contactEmail ?? '',
            contactPhone: res.data.contactPhone ?? ''
          };
          reset(data);
          setFetchedCompany(data);
        } catch (error) {
          reset(defaultValues);
          console.error(error);
        }
      })();
    } else {
      reset(defaultValues);
    }
  }, [defaultValues, reset, companyId])

  const onSubmit = handleSubmit(async (data) => {
    try {
      let res;
      if (isEditMode) {
        res = await updateCompanyDetails({ ...fetchedCompany, ...data });
        mutate(prev => prev?.map(membership => membership.id === res.data.id ? res.data : membership), false);
      } else {
        res = await createCompany(data);
        mutate(prev => [...prev, res.data].sort((a, b) => a.company.localeCompare(b.company)), false);
      }

      toast.success(res.message);
      onClose();

    } catch (error) {
      toast.error(error.message);
      console.error(error);
    }
  })

  const clearLogo = () => {
    methods.setValue('formFile', null);
    setFetchedCompany(prev => ({ ...prev, logoPath: null }));
  };


  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md' TransitionProps={{ onExited: () => { onExited(); reset(defaultValues); } }}>
      <Form methods={methods} onSubmit={onSubmit}>
        <DialogTitle>{isEditMode ? t('editCompany') : t('newCompany')}</DialogTitle>
        <DialogContent>
          <Box display='grid' gridTemplateColumns='1fr 1fr' gap={2} alignItems='center'>
            <Stack spacing={2} pt={1}>
              <Field.Text name='name' label={t('companyName')} required />
              <Field.Select name='countryId' label={t('country')} required>
                {countries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </Field.Select>
              <Field.Select name='currencyId' label={t('currency')} required>
                {currencies.map((currency) => (
                  <MenuItem key={currency.id} value={currency.id}>
                    {currency.name}
                  </MenuItem>
                ))}
              </Field.Select>
              <Field.Text name='websiteUrl' label={t('websiteUrl')} placeholder="https://www.auditpulse.uk" />
            </Stack>

            <Grid item display='flex' flexDirection='column' alignItems='center' gap={2}>
              <Field.UploadAvatar
                name="formFile"
                path={fetchedCompany?.logoPath}
                maxSize={3145728}
                text="logo"
                helperText={
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 3,
                      mx: 'auto',
                      display: 'block',
                      textAlign: 'center',
                      color: 'text.disabled',
                    }}
                  >
                    Allowed *.jpeg, *.jpg, *.png
                    <br /> max size of {fData(3145728)}
                  </Typography>
                }
              />
              <Button
                variant="outlined"
                color="error"
                onClick={clearLogo}
              >
                {t('delete')}
              </Button>
            </Grid>

            <Divider sx={{ gridColumn: '1 / -1' }} />

            <Field.Text name='contactName' label={t('contactName')} />
            <Field.Switch name='showContactName' label={t('showName')} />
            <Field.Text name='contactEmail' label={t('contactEmail')} />
            <Field.Switch name='showContactEmail' label={t('showEmail')} />
            <Field.Phone name='contactPhone' label={t('contactPhone')} country='PK' />
            <Field.Switch name='showContactPhone' label={t('showPhone')} />
          </Box>
        </DialogContent>
        <DialogActions>
          <CloseButton onClick={onClose}>{t('cancel')}</CloseButton>
          <LoadingButton variant="contained" type="submit" loading={isSubmitting}>{isEditMode ? t('update') : t('add')}</LoadingButton>
        </DialogActions>
      </Form>
    </Dialog>
  )
}

export default CompanyDialog;